import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LifeBuoy, Plus, MessageSquare } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const statusCls = { open: "bg-brand-cyan/10 text-brand-cyan", pending: "bg-amber-500/10 text-amber-400", closed: "bg-slate-500/10 text-slate-400" };

export default function Tickets() {
  const { t } = useLang();
  useSEO("Support");
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "general", message: "" });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/tickets").then((r) => setTickets(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/tickets", form);
      toast.success("Ticket created");
      setForm({ subject: "", category: "general", message: "" });
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-white">{t("support")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gradient-brand text-brand-navy font-semibold" data-testid="new-ticket-btn"><Plus className="h-4 w-4 mr-1.5" /> New Ticket</Button></DialogTrigger>
          <DialogContent className="bg-brand-surface border-white/10">
            <DialogHeader><DialogTitle className="text-white">Create Support Ticket</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4" data-testid="ticket-form">
              <div><Label className="text-slate-300">Subject</Label><Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ticket-subject" className="mt-1.5 bg-white/5 border-white/10" /></div>
              <div><Label className="text-slate-300">Message</Label><Textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ticket-message" className="mt-1.5 bg-white/5 border-white/10" /></div>
              <Button type="submit" disabled={loading} data-testid="ticket-submit" className="w-full gradient-brand text-brand-navy font-semibold">{loading ? t("loading") : t("submit")}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {tickets.length === 0 ? (
        <div className="card-surface rounded-2xl p-12 text-center">
          <LifeBuoy className="mx-auto h-10 w-10 text-slate-600 mb-3" />
          <p className="text-slate-500">No support tickets yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => (
            <Link key={ticket.id} to={`/dashboard/tickets/${ticket.id}`} className="card-surface hover-glow rounded-2xl p-5 flex items-center justify-between" data-testid={`ticket-${ticket.id}`}>
              <div className="flex items-center gap-3">
                <MessageSquare className="h-5 w-5 text-brand-cyan" />
                <div>
                  <p className="font-display font-semibold text-white">{ticket.subject}</p>
                  <p className="text-xs text-slate-500">{ticket.messages?.length || 0} messages · {new Date(ticket.updated_at).toLocaleDateString()}</p>
                </div>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-medium capitalize ${statusCls[ticket.status] || statusCls.open}`}>{ticket.status}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
